import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  AnalyticsEvent,
  AnalyticsService,
  ApiCallingV2Service,
  API_CONFIG,
  LotOrDeal,
  lotOrDeal,
  withDeal,
  withLot
} from '@zayan-erp/shared';
import { BehaviorSubject, catchError, map, Observable, of, Subject, tap } from 'rxjs';

import {
  CancelDeal,
  ClosingDatesPayload,
  ContractDetailsPayload,
  CostDetailsPayload,
  PricingTypePayload,
  Deal,
  BuyerSelection,
  BuyerContract,
  ApsDealDocument,
  DocumentTemplate,
  ModelImport,
  Prospect,
  ReservationPayload,
  DealLite,
  DealsLiteResponse,
  miscellaneousDocument,
  DealDetailLite,
  DealLotChangeRequest,
  DealLotChangeResult
} from '@zayan-erp/sales';
import { FormControl, FormGroup } from '@angular/forms';
import { DealIncentive } from '../models/incentive.models';
import { SendWelcomeEmailOptions, WelcomeEmailPreview, WelcomeEmailStatusDto } from '../models/email-templates.models';

export interface ModelElevationEligibilityResult {
  isEligible: boolean;
  violatedRules: string[];
}

@Injectable({
  providedIn: 'root'
})
export class SalesApsService {
  #http = inject(ApiCallingV2Service);
  #httpClient = inject(HttpClient);
  #apiConfig = inject(API_CONFIG);
  #analytics = inject(AnalyticsService);
  baseUrl = this.#apiConfig.baseUrlV2;

  constructor() {}
  dealsUpdated = new BehaviorSubject<boolean>(false);
  dealsUpdated$ = this.dealsUpdated.asObservable();

  // reloadDeal = signal<boolean>(false);
  isReserved = signal<boolean>(false);
  /** Currently selected deal id from detail sidebar. */
  dealSelectionVersion = signal<string>('');

  /** Current deal when viewing deal detail (set by parent, read by routed tab components). */
  currentDeal = signal<DealDetailLite | null>(null);

  readonly form = new FormGroup({
    closingDate: new FormControl<Date>(new Date())
  });

  realoadDeal = new Subject<void | null>();
  realoadDeal$ = this.realoadDeal.asObservable();

  /**
   * Analytics context for a known dealId.
   * Attaches lot_id from currentDeal only when that deal is the one open.
   */
  dealCtx(dealId: string): LotOrDeal {
    const deal = this.currentDeal();
    const lotId = deal?.id === dealId ? deal.lot?.id : undefined;
    return withDeal(dealId, lotId);
  }

  /**
   * Analytics context from explicit ids and/or the open currentDeal.
   * Prefer passing known ids; missing ones fall back to currentDeal.
   */
  currentDealCtx(ids?: { lotId?: string | null; dealId?: string | null }): LotOrDeal | null {
    const deal = this.currentDeal();
    return lotOrDeal({
      lotId: ids?.lotId ?? deal?.lot?.id,
      dealId: ids?.dealId ?? deal?.id
    });
  }
  getDealsLite(
    statusIds?: string[],
    projectId?: string,
    dateFilters?: {
      fromContractDate?: string;
      toContractDate?: string;
      fromTargetClosingDate?: string;
      toTargetClosingDate?: string;
    },
    showLoader = true,
    lotId?: string,
    silent = false,
    pageSize = 1000
  ): Observable<DealsLiteResponse> {
    const params: string[] = [];
    if (statusIds) {
      for (const statusId of statusIds) {
        params.push(`Status=${statusId}`);
      }
    }
    if (projectId) params.push(`ProjectId=${projectId}`);
    if (lotId) params.push(`LotId=${lotId}`);
    if (dateFilters?.fromContractDate) params.push(`FromContractDate=${dateFilters.fromContractDate}`);
    if (dateFilters?.toContractDate) params.push(`ToContractDate=${dateFilters.toContractDate}`);
    if (dateFilters?.fromTargetClosingDate) params.push(`FromTargetClosingDate=${dateFilters.fromTargetClosingDate}`);
    if (dateFilters?.toTargetClosingDate) params.push(`ToTargetClosingDate=${dateFilters.toTargetClosingDate}`);
    params.push(`PageSize=${pageSize}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.#http.getData<DealsLiteResponse>(`${this.baseUrl}/deals/lite${queryString}`, {
      showLoader: silent ? false : showLoader,
      showMessage: false,
      showError: !silent
    });
  }

  getDeals(status?: string, projectId?: string): Observable<Deal[]> {
    const params: string[] = [];
    if (status) params.push(`Status=${status}`);
    if (projectId) params.push(`ProjectId=${projectId}`);
    params.push(`PageSize=${1000}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.#http.getData<Deal[]>(`${this.baseUrl}/deals${queryString}`, { showLoader: true, showMessage: false });
  }

  getDealById(id: string, showLoader = true): Observable<DealDetailLite> {
    return this.#http.getData<DealDetailLite>(`${this.baseUrl}/deals/${id}`, { showLoader, showMessage: false });
  }

  getDealsByProspectId(prospectId: string, showLoader = true): Observable<Deal[]> {
    return this.#http.getData<Deal[]>(`${this.baseUrl}/deals/prospect/${prospectId}`, {
      showLoader,
      showMessage: false
    });
  }
  getDealsLiteByProspectId(prospectId: string): Observable<DealLite[]> {
    return this.#http.getData<DealLite[]>(`${this.baseUrl}/deals/lite/prospect/${prospectId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  getModels(lotId?: string): Observable<ModelImport[]> {
    if (!lotId) {
      return this.#http.getData<ModelImport[]>(`${this.baseUrl}/Model`, { showLoader: false, showMessage: false });
    }
    return this.#http.getData<ModelImport[]>(`${this.baseUrl}/Lot/${lotId}/models`, {
      showLoader: true,
      showMessage: false
    });
  }

  getProspects(
    page: number = 1,
    pageSize: number = 20,
    salesOfficeId: string | null = null,
    search: string = ''
  ): Observable<Prospect[]> {
    const params = `?pageNumber=${page}&pageSize=${pageSize}&salesOfficeId=${salesOfficeId ?? ''}&search=${search}`;
    return this.#http.getData<Prospect[]>(`${this.baseUrl}/Prospect${params}`, {
      showLoader: true,
      showMessage: false
    });
  }

  getProspectDeals(prospectId: string): Observable<any> {
    return this.getDealsByProspectId(prospectId);
  }

  createDeal(payload: {
    salesOfficeId: string;
    lotId: string;
    modelId: string;
    elevationId: string;
    prospectId: string;
    projectId?: string;
    realtorAgentId?: string;
  }): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deals/create`,
        { showLoader: true, showMessage: true, message: 'Deal created successfully' },
        payload
      )
      .pipe(
        tap((result: any) => {
          const dealId = result?.id ?? result?.dealId;
          this.#analytics.capture(AnalyticsEvent.DealCreated, withLot(payload.lotId, dealId));
        })
      );
  }

  updateDeal(
    dealId: string,
    payload: {
      salesOfficeId: string;
      lotId: string;
      modelId: string;
      elevationId: string;
      prospectId: string;
      projectId?: string;
      realtorAgentId?: string;
    }
  ): Observable<any> {
    return this.#http
      .putData(`${this.baseUrl}/deals/${dealId}`, payload, {
        showLoader: true,
        showMessage: true,
        message: 'Deal updated successfully'
      })
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  markContractReady(dealId: string): Observable<any> {
    return this.#http
      .putData(
        `${this.baseUrl}/deals/${dealId}/contract-ready`,
        {},
        {
          showLoader: true,
          buttonId: 'deal-mark-contract-ready',
          showMessage: true,
          message: 'Contract marked as ready successfully'
        }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealContractReadyMarked, this.dealCtx(dealId));
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  cancelDeal(dealId: string, cancelReason: CancelDeal): Observable<any> {
    return this.#http
      .patchData(`${this.baseUrl}/deals/${dealId}/cancel`, cancelReason, {
        showLoader: true,
        showMessage: true,
        message: 'Deal cancelled successfully'
      })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealCancelled, {
            ...this.dealCtx(dealId),
            cancel_reason: cancelReason.reason
          });
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  reserveDeal(dealId: string, payload: ReservationPayload, buttonId?: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deals/${dealId}/reserve`,
        {
          showLoader: true,
          buttonId,
          showMessage: true,
          message: 'Deal reserved successfully'
        },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealReserved, this.dealCtx(dealId));
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  revertContractReady(dealId: string): Observable<any> {
    return this.#http
      .putData(
        `${this.baseUrl}/deals/${dealId}/revert-contract-ready`,
        {},
        {
          showLoader: true,
          buttonId: 'deal-revert-contract-ready',
          showMessage: true,
          message: 'Deal reverted to Selection status successfully'
        }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealContractReadyReverted, this.dealCtx(dealId));
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  updatePricing(dealId: string, payload: CostDetailsPayload): Observable<any> {
    return this.#http
      .patchData(`${this.baseUrl}/deals/${dealId}/pricing`, payload, {
        showLoader: true,
        buttonId: 'deal-save-pricing',
        showMessage: true,
        message: 'Pricing updated successfully'
      })
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  updatePricingType(dealId: string, payload: PricingTypePayload): Observable<any> {
    return this.#http
      .putData(`${this.baseUrl}/deals/${dealId}/pricing-type`, payload, {
        showLoader: true,
        showMessage: true,
        message: 'Pricing type updated successfully'
      })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealPricingTypeUpdated, {
            ...this.dealCtx(dealId),
            pricing_type: payload.pricingType
          });
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  updateClosingDates(contractId: string, payload: ClosingDatesPayload): Observable<any> {
    return this.#http
      .patchData(`${this.baseUrl}/contracts/${contractId}/closing-dates`, payload, {
        showLoader: true,
        buttonId: 'deal-save-closing',
        showMessage: true,
        message: 'Closing dates updated successfully'
      })
      .pipe(
        tap(() => {
          const ctx = this.currentDealCtx();
          if (ctx) {
            this.#analytics.capture(AnalyticsEvent.DealClosingDatesUpdated, {
              ...ctx,
              contract_id: contractId
            });
          }
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  updateContract(dealId: string, payload: ContractDetailsPayload): Observable<any> {
    return this.#http
      .patchData(`${this.baseUrl}/deals/${dealId}/contract`, payload, {
        showLoader: true,
        buttonId: 'deal-save-contract-details',
        showMessage: true,
        message: 'Contract details updated successfully'
      })
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  approveContract(dealId: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deals/${dealId}/approve-contract`,
        {
          showLoader: true,
          buttonId: 'deal-submit-approval',
          showMessage: true,
          message: 'Contract submitted for approval successfully'
        },
        {}
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealContractApproved, this.dealCtx(dealId));
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  markApproved(dealId: string, approvalDate: string): Observable<any> {
    return this.#http
      .postData(
        `${this.baseUrl}/deals/${dealId}/mark-approved`,
        {
          showLoader: true,
          buttonId: 'deal-save-contract-details',
          showMessage: true,
          message: 'Contract marked as approved successfully'
        },
        { approvalDate }
      )
      .pipe(
        tap(() => {
          const props = this.dealCtx(dealId);
          this.#analytics.capture(AnalyticsEvent.DealMarkedApproved, props);
          this.#analytics.capture(AnalyticsEvent.DealMarkedUnderContract, props);
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  withdrawApproval(dealId: string): Observable<any> {
    return this.#http
      .putData(
        `${this.baseUrl}/deals/${dealId}/withdraw-approval`,
        {},
        {
          showLoader: true,
          buttonId: 'deal-withdraw-approval',
          showMessage: true,
          message: 'Approval withdrawn successfully'
        }
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealApprovalWithdrawn, this.dealCtx(dealId));
          this.dealsUpdated.next(!this.dealsUpdated.value);
        })
      );
  }

  getDealDocuments(dealId: string, showLoader = true): Observable<ApsDealDocument[]> {
    return this.#http
      .getData<
        ApsDealDocument | ApsDealDocument[]
      >(`${this.baseUrl}/deal-documents/deal/${dealId}/contract`, { showLoader, showMessage: false })
      .pipe(map(data => (Array.isArray(data) ? data : data ? [data] : [])));
  }

  getDocumentTemplatesByProject(projectId: string): Observable<DocumentTemplate[]> {
    return this.#http.getData<DocumentTemplate[]>(`${this.baseUrl}/document-templates/project/${projectId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  getProjectNonApsDocuments(projectId: string): Observable<miscellaneousDocument[]> {
    return this.#http.getData<DocumentTemplate[]>(`${this.baseUrl}/document-templates/project/${projectId}/non-aps`, {
      showLoader: true,
      showMessage: false
    });
  }

  getDocumentFileUrl(documentId: string): Observable<string> {
    return this.#http
      .getData<{ Url?: string; url?: string }>(`${this.baseUrl}/filesystem/${documentId}/fileUrl`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(data => data?.Url ?? data?.url ?? ''));
  }

  generateSchedulePeDocument(
    dealId: string
  ): Observable<{ documentId: string; documentName: string; documentUrl?: string | null }> {
    return this.#http
      .postData<{ documentId?: string; documentName?: string; directUrl?: string | null }>(
        `${this.baseUrl}/deal-documents/deal/${dealId}/generate-schedule-pe`,
        {
          showLoader: true,
          showMessage: true,
          message: 'Addendums fetched successfully'
        }
      )
      .pipe(
        map(response => {
          const mappedUrl = response?.directUrl;
          return {
            documentId: response?.documentId ?? '',
            documentName: response?.documentName ?? '',
            documentUrl: mappedUrl
          };
        })
      );
  }

  generateStampedLegalPlan(
    lotId: string
  ): Observable<{ documentId: string; documentName: string; documentUrl?: string | null }> {
    return this.#http
      .postData<{
        documentId?: string;
        documentName?: string;
        downloadUrl?: string | null;
      }>(
        `${this.baseUrl}/legalcoordinatemapping/lots/${lotId}/stamped-pdf`,
        { showLoader: true, showMessage: false },
        {}
      )
      .pipe(
        map(response => {
          const mappedUrl = response?.downloadUrl;
          return {
            documentId: response?.documentId ?? '',
            documentName: response?.documentName ?? '',
            documentUrl: mappedUrl
          };
        })
      );
  }

  getDealIncentive(dealId: string): Observable<DealIncentive | null> {
    return this.#http
      .getData<DealIncentive>(`${this.baseUrl}/deal-incentives/deal/${dealId}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(
        map(result => (result && typeof result === 'object' && 'id' in result ? result : null)),
        catchError(() => of(null))
      );
  }

  createDealIncentive(payload: { dealId: string } & Omit<DealIncentive, 'id' | 'dealId'>): Observable<DealIncentive> {
    return this.#http
      .postData<DealIncentive>(
        `${this.baseUrl}/deal-incentives`,
        {
          showLoader: true,
          buttonId: 'deal-save-incentive',
          showMessage: true,
          message: 'Incentive created successfully'
        },
        payload
      )
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  updateDealIncentive(incentiveId: string, incentive: Omit<DealIncentive, 'id' | 'dealId'>): Observable<DealIncentive> {
    return this.#http
      .putData<DealIncentive>(`${this.baseUrl}/deal-incentives/${incentiveId}`, incentive, {
        showLoader: true,
        buttonId: 'deal-save-incentive',
        showMessage: true,
        message: 'Incentives updated successfully'
      })
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  deleteDealIncentive(incentiveId: string): Observable<any> {
    return this.#http
      .deleteData(`${this.baseUrl}/deal-incentives/${incentiveId}`, {
        showLoader: true,
        showMessage: true,
        message: 'Incentive deleted successfully'
      })
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  viewSignedDocument(envelopeId: string): Observable<Blob> {
    return this.#httpClient.get(`${this.baseUrl}/deal-documents/${envelopeId}/download-signed`, {
      responseType: 'blob'
    });
  }

  uploadSignedPdfDocument(file: File): Observable<{ presignedUrl: string; s3Key: string; documentId: string }> {
    const body = JSON.stringify(file.name);
    return this.#http
      .postData<{
        uploadUrl: string;
        s3Key: string;
        documentId: string;
      }>(`${this.baseUrl}/documents/private/upload-url`, { showLoader: true, showMessage: true }, body)
      .pipe(
        map(data => ({
          presignedUrl: data.uploadUrl,
          s3Key: data.s3Key,
          documentId: data.documentId
        }))
      );
  }

  updateDocumentSignerStatus(dealDocumentId: string, signerId: string, signedDocumentId: string): Observable<any> {
    return this.#http.patchData(
      `${this.baseUrl}/deal-documents/${dealDocumentId}/signers/${signerId}/signed`,
      { signedDocumentId },
      { showLoader: true, showMessage: false }
    );
  }

  waiveSigner(dealDocumentId: string, signerId: string): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/deal-documents/${dealDocumentId}/signers/${signerId}/waive`,
      { showLoader: true, showMessage: true, message: 'Signer waived and converted to carbon copy in DocuSign.' },
      {}
    );
  }

  sendWelcomeEmail(dealId: string, skip = false, options?: SendWelcomeEmailOptions): Observable<any> {
    return this.#http.postData(
      `${this.baseUrl}/deals/${dealId}/buyers/send-welcome-email`,
      {
        showLoader: true,
        showMessage: true,
        message: skip ? 'Welcome email skipped successfully' : 'Welcome email sent successfully'
      },
      {
        skipEmail: skip,
        attachAps: options?.attachAps ?? false,
        customDocuments: options?.customDocuments ?? []
      }
    );
  }

  getWelcomeEmailPreview(dealId: string): Observable<WelcomeEmailPreview> {
    return this.#http.getData<WelcomeEmailPreview>(`${this.baseUrl}/deals/${dealId}/welcome-email/preview`, {
      showLoader: false,
      showMessage: false
    });
  }

  welcomeEmailStatus(dealId: string, showLoader = true): Observable<any> {
    return this.#http.getData(`${this.baseUrl}/deals/${dealId}/welcome-email-status`, {
      showLoader,
      showMessage: false
    });
  }

  formatSigneeType(entityType: string): string {
    if (entityType.toLowerCase?.() === 'user') {
      return 'Executive';
    }

    if (entityType.toLowerCase?.().includes('cobuyer')) {
      return 'Co-Buyer';
    }
    if (entityType.toLowerCase?.().includes('prospect')) {
      return 'Prospect';
    }
    if (entityType.toLowerCase?.().includes('realtor')) {
      return 'Realtor';
    }
    if (entityType.toLowerCase?.().includes('secondary_executive')) {
      return 'Secondary Executive';
    }
    return entityType;
  }

  checkRuntimeEligibility(params: {
    lotId: string;
    modelId: string | null;
    elevationId: string | null;
    scope?: number;
  }): Observable<ModelElevationEligibilityResult> {
    const query =
      `?lotId=${params.lotId}&modelId=${params.modelId}&elevationId=${params.elevationId}` +
      (params.scope != null ? `&scope=${params.scope}` : '');

    return this.#http.getData<ModelElevationEligibilityResult>(
      `${this.baseUrl}/Lot/model-elevation-eligibility${query}`,
      {
        showLoader: true,
        showMessage: false
      }
    );
  }

  updateDealModelElevation(
    dealId: string,
    payload: { lotId: string; modelId: string; elevationId: string }
  ): Observable<any> {
    // Read before the request: a successful save reloads currentDeal with the new lot.
    const deal = this.currentDeal();
    const previousLotId = deal?.id === dealId ? deal.lot?.id : undefined;

    return this.#http
      .patchData(`${this.baseUrl}/deals/${dealId}`, payload, { showLoader: true, showMessage: false })
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.DealLotInfoUpdated, {
            ...withLot(previousLotId ?? payload.lotId, dealId),
            new_lot_id: payload.lotId,
            model_id: payload.modelId,
            elevation_id: payload.elevationId
          });
        })
      );
  }

  changeLotPreview(dealId: string, body: DealLotChangeRequest): Observable<DealLotChangeResult> {
    return this.#http.postData<DealLotChangeResult>(
      `${this.baseUrl}/deals/${dealId}/change-lot/preview`,
      { showLoader: true, showMessage: false, showError: false, buttonId: 'change-lot-preview' },
      body
    );
  }

  changeLot(dealId: string, body: DealLotChangeRequest): Observable<DealLotChangeResult> {
    return this.#http
      .postData<DealLotChangeResult>(
        `${this.baseUrl}/deals/${dealId}/change-lot`,
        {
          showLoader: true,
          showMessage: true,
          showError: false,
          message: 'Lot changed',
          buttonId: 'change-lot-confirm'
        },
        body
      )
      .pipe(tap(() => this.dealsUpdated.next(!this.dealsUpdated.value)));
  }

  getBuyerSelection(dealId: string, showLoader = false): Observable<BuyerSelection> {
    return this.#http.getData<BuyerSelection>(`${this.baseUrl}/deals/${dealId}/buyer-selections`, {
      showLoader,
      showMessage: false
    });
  }

  getBuyerContract(dealId: string, showLoader = false): Observable<BuyerContract> {
    return this.#http.getData<BuyerContract>(`${this.baseUrl}/deals/${dealId}/buyer-contracts`, {
      showLoader,
      showMessage: false
    });
  }
}
